import { useState, type FC, type KeyboardEvent } from 'react';
import dayjs from 'dayjs';
import {
  IpcDatePicker,
  type IpcDateDisplayFormat,
  type IpcDatePickerVariant,
} from './IpcDatePicker';

export type IpcEditableDateProps = {
  value?: string | null;
  onSave: (value: string | undefined) => void;
  saving?: boolean;
  disabled?: boolean;
  /** 显示态 / 编辑态展示格式；默认 YYYY-MM-DD */
  format?: IpcDateDisplayFormat;
  emptyText?: string;
  className?: string;
  /** 编辑控件 variant，默认 cell */
  pickerVariant?: Extract<IpcDatePickerVariant, 'cell' | 'inline'>;
  placeholder?: string;
};

function formatDisplay(value: string | null | undefined, format: IpcDateDisplayFormat, emptyText: string): string {
  if (!value) return emptyText;
  const d = dayjs(value);
  if (!d.isValid()) return emptyText;
  return d.format(format);
}

/**
 * 表格行内日期：点按进入编辑，高度与显示态一致，选完/失焦保存
 */
export const IpcEditableDate: FC<IpcEditableDateProps> = ({
  value,
  onSave,
  saving = false,
  disabled = false,
  format = 'YYYY-MM-DD',
  emptyText = '—',
  className,
  pickerVariant = 'cell',
  placeholder = '选择日期',
}) => {
  const [editing, setEditing] = useState(false);

  if (!editing) {
    return (
      <span
        className={[
          'ipc-editable-date',
          saving ? 'ipc-editable-date--saving' : '',
          disabled ? 'ipc-editable-date--disabled' : '',
          className,
        ].filter(Boolean).join(' ')}
        onClick={() => {
          if (!disabled && !saving) setEditing(true);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (disabled || saving) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setEditing(true);
          }
        }}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        title={disabled ? undefined : '点击编辑日期'}
      >
        {formatDisplay(value, format, emptyText)}
      </span>
    );
  }

  return (
    <IpcDatePicker
      variant={pickerVariant}
      format={format}
      value={value}
      placeholder={placeholder}
      disabled={saving}
      autoFocus
      autoOpen
      allowClear
      className={['ipc-editable-date__picker', className].filter(Boolean).join(' ')}
      onChange={next => {
        setEditing(false);
        onSave(next);
      }}
      onOpenChange={open => {
        if (!open) setEditing(false);
      }}
    />
  );
};
